import { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Plus, Archive, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

export default function PalletStatement({ user, onLogout }) {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Archived customers
  const [showArchivedCustomers, setShowArchivedCustomers] = useState(false);
  const [archivedCustomers, setArchivedCustomers] = useState([]);
  const [archivedCustomersLoading, setArchivedCustomersLoading] = useState(false);
  const [expandedArchived, setExpandedArchived] = useState({});
  
  // Pallet monitoring form (Admin only)
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [colour, setColour] = useState('White');
  const [direction, setDirection] = useState('OUT');
  const [quantity, setQuantity] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [showMonitoringForm, setShowMonitoringForm] = useState(false);
  
  // New customer form
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newCustomerAddress, setNewCustomerAddress] = useState('');
  const [newCustomerEmail, setNewCustomerEmail] = useState('');

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchArchivedCustomers = async () => {
    setArchivedCustomersLoading(true);
    try {
      // Fetch ALL customers
      const allCustomersResponse = await api.get('/customers', {
        params: { with_outstanding_only: false }
      });
      
      // Calculate which ones have zero balance
      const customersWithZeroBalance = [];
      
      for (const customer of allCustomersResponse.data) {
        try {
          const statementResponse = await api.get(`/pallet-movements/customer/${customer.id}`);
          const statement = statementResponse.data;
          
          // Check if total chargeable is 0
          if (statement.total_chargeable === 0 && statement.statement.length > 0) {
            customersWithZeroBalance.push({
              ...customer,
              movements: statement.statement,
              balances: statement.balances
            });
          }
        } catch (error) {
          console.error(`Error fetching statement for ${customer.business_name}:`, error);
        }
      }
      
      setArchivedCustomers(customersWithZeroBalance);
    } catch (error) {
      toast.error('Failed to load archived customers');
      console.error(error);
    } finally {
      setArchivedCustomersLoading(false);
    }
  };

  const toggleArchivedCustomer = (customerId) => {
    setExpandedArchived(prev => ({
      ...prev,
      [customerId]: !prev[customerId]
    }));
  };

  const fetchCustomers = async () => {
    try {
      // Fetch only customers with outstanding pallets for the dropdown
      const response = await api.get('/customers', {
        params: { with_outstanding_only: true }
      });
      let allCustomers = response.data;
      
      // Filter out demo/test customers created by system
      allCustomers = allCustomers.filter(c => {
        const name = c.business_name.toLowerCase();
        return !name.includes('demo') && 
               !name.includes('test') && 
               !name.includes('example') &&
               !name.includes('sample');
      });
      
      // Consolidate customers with similar names (e.g., multiple "Wobbly Supermarket" entries)
      const consolidatedMap = new Map();
      
      for (const customer of allCustomers) {
        const businessName = customer.business_name.trim();
        
        // Check if we already have this business name
        if (consolidatedMap.has(businessName)) {
          // Keep the first one we found
          continue;
        } else {
          consolidatedMap.set(businessName, customer);
        }
      }
      
      // Convert map back to array and sort alphabetically
      const uniqueCustomers = Array.from(consolidatedMap.values()).sort((a, b) => 
        a.business_name.localeCompare(b.business_name)
      );
      
      setCustomers(uniqueCustomers);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchStatement = async (customerId) => {
    try {
      const response = await api.get(`/pallet-movements/customer/${customerId}`);
      setStatement(response.data);
    } catch (error) {
      toast.error('Failed to load pallet statement');
    }
  };

  const handleCustomerChange = (customerId) => {
    setSelectedCustomer(customerId);
    if (customerId) {
      fetchStatement(customerId);
    } else {
      setStatement(null);
    }
  };

  const handleMonitoringSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCustomer) {
      toast.error('Please select a customer first');
      return;
    }
    
    if (!quantity || parseInt(quantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    try {
      await api.post('/pallet-movements', {
        customer_id: selectedCustomer,
        date: date,
        colour: colour,
        direction: direction,
        quantity: parseInt(quantity),
        reference: reference,
        notes: notes
      });
      
      toast.success('Pallet movement recorded successfully');
      
      // Reset form
      setQuantity('');
      setReference('');
      setNotes('');
      setShowMonitoringForm(false);
      
      // Refresh statement
      fetchStatement(selectedCustomer);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record pallet movement');
    }
  };

  const handleCreateCustomer = async (e) => {
    e.preventDefault();
    
    if (!newCustomerName || !newCustomerAddress) {
      toast.error('Please fill in business name and address');
      return;
    }

    try {
      const response = await api.post('/customers', {
        business_name: newCustomerName,
        delivery_address: newCustomerAddress,
        email: newCustomerEmail || undefined
      });
      
      toast.success('Customer created successfully');
      
      // Refresh customers list
      await fetchCustomers();
      
      // Select the new customer
      setSelectedCustomer(response.data.id);
      
      // Reset and close form
      setNewCustomerName('');
      setNewCustomerAddress('');
      setNewCustomerEmail('');
      setShowNewCustomerForm(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create customer');
    }
  };

  const generateProforma = () => {
    if (!statement || !selectedCustomer) {
      toast.error('No statement data available');
      return;
    }

    const customer = customers.find(c => c.id === selectedCustomer);
    if (!customer) return;

    // Calculate totals with VAT breakdown
    const basePrice = 4.17; // Base price before VAT
    const vatRate = 0.20; // 20% VAT
    const priceWithVAT = 5.00; // Total price including VAT
    
    const palletPrices = { White: 0, Red: priceWithVAT, Blue: priceWithVAT };
    
    const outstandingRed = statement.balances.Red || 0;
    const outstandingBlue = statement.balances.Blue || 0;
    
    // Calculate subtotals
    const redSubtotal = outstandingRed * basePrice;
    const blueSubtotal = outstandingBlue * basePrice;
    const subtotal = redSubtotal + blueSubtotal;
    
    const vatAmount = subtotal * vatRate;
    const totalOwed = subtotal + vatAmount;

    // Generate HTML for printing
    const proformaHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Pallet Proforma - ${customer.business_name}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 40px; }
          h1 { color: #1e3a8a; border-bottom: 3px solid #1e3a8a; padding-bottom: 10px; }
          .info { margin: 20px 0; }
          .info strong { color: #475569; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th { background-color: #f1f5f9; color: #1e3a8a; padding: 12px; text-align: left; border-bottom: 2px solid #cbd5e1; }
          td { padding: 10px; border-bottom: 1px solid #e2e8f0; }
          .out { color: #dc2626; font-weight: 600; }
          .in { color: #16a34a; font-weight: 600; }
          .balance { font-weight: bold; color: #1e3a8a; }
          .summary { margin-top: 30px; padding: 20px; background-color: #f8fafc; border: 2px solid #1e3a8a; }
          .summary-row { display: flex; justify-content: space-between; margin: 10px 0; font-size: 16px; }
          .total { font-size: 24px; font-weight: bold; color: #1e3a8a; margin-top: 15px; padding-top: 15px; border-top: 2px solid #1e3a8a; }
          .pricing { margin: 20px 0; padding: 15px; background-color: #dbeafe; border-radius: 8px; }
        </style>
      </head>
      <body>
        <h1>Pallet Statement</h1>
        <div class="info">
          <p><strong>Customer:</strong> ${customer.business_name}</p>
          <p><strong>Address:</strong> ${customer.delivery_address}</p>
          <p><strong>Date Generated:</strong> ${new Date().toLocaleDateString('en-GB')}</p>
        </div>
        
        <div class="pricing">
          <strong>Pallet Pricing:</strong>
          <ul>
            <li>White Pallets: £0.00 (Free)</li>
            <li>Red Pallets: £4.17 + VAT (20%) = £5.00 per pallet</li>
            <li>Blue Pallets: £4.17 + VAT (20%) = £5.00 per pallet</li>
          </ul>
        </div>

        <table>
          <thead>
            <tr>
              <th>Date</th>
              <th>Reference</th>
              <th>Colour</th>
              <th style="text-align: right;">OUT</th>
              <th style="text-align: right;">IN</th>
              <th style="text-align: right;">Balance</th>
            </tr>
          </thead>
          <tbody>
            ${statement.statement.map(movement => `
              <tr>
                <td>${new Date(movement.date).toLocaleDateString('en-GB')}</td>
                <td>${movement.reference}</td>
                <td>${movement.colour}</td>
                <td style="text-align: right;" class="out">${movement.direction === 'OUT' ? movement.quantity : '-'}</td>
                <td style="text-align: right;" class="in">${movement.direction === 'IN' ? movement.quantity : '-'}</td>
                <td style="text-align: right;" class="balance">${movement.running_balance}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="summary">
          <h2 style="color: #1e3a8a; margin-top: 0;">Outstanding Pallets Summary</h2>
          <div class="summary-row">
            <span>White Pallets Outstanding:</span>
            <span class="balance">${statement.balances.White || 0} @ £0.00 = £0.00</span>
          </div>
          <div class="summary-row">
            <span>Red Pallets Outstanding:</span>
            <span class="balance">${outstandingRed} × £4.17 = £${redSubtotal.toFixed(2)}</span>
          </div>
          <div class="summary-row">
            <span>Blue Pallets Outstanding:</span>
            <span class="balance">${outstandingBlue} × £4.17 = £${blueSubtotal.toFixed(2)}</span>
          </div>
          <div class="summary-row" style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #cbd5e1;">
            <span>Subtotal (exc. VAT):</span>
            <span class="balance">£${subtotal.toFixed(2)}</span>
          </div>
          <div class="summary-row">
            <span>VAT (20%):</span>
            <span class="balance">£${vatAmount.toFixed(2)}</span>
          </div>
          <div class="total">
            <div class="summary-row">
              <span>TOTAL AMOUNT OWED (inc. VAT):</span>
              <span>£${totalOwed.toFixed(2)}</span>
            </div>
          </div>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(proformaHTML);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="text-center py-12">Loading...</div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-4 sm:space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">Pallet Statement</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-2">Track pallet movements and balances per customer</p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <CardTitle className="text-lg sm:text-xl">Select Customer</CardTitle>
              <Button 
                type="button"
                variant="outline" 
                size="sm"
                onClick={() => setShowNewCustomerForm(!showNewCustomerForm)}
                className="w-full sm:w-auto"
              >
                <Plus className="h-4 w-4 mr-1" />
                New Customer
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Select value={selectedCustomer} onValueChange={handleCustomerChange}>
              <SelectTrigger data-testid="customer-select">
                <SelectValue placeholder="Choose a customer..." />
              </SelectTrigger>
              <SelectContent>
                {customers.map((customer) => (
                  <SelectItem key={customer.id} value={customer.id}>
                    {customer.business_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* New Customer Form */}
        {showNewCustomerForm && (
          <Card className="border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle>Create New Customer</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateCustomer} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="newBusinessName" className="text-sm">Business Name *</Label>
                    <Input
                      id="newBusinessName"
                      value={newCustomerName}
                      onChange={(e) => setNewCustomerName(e.target.value)}
                      placeholder="Enter business name"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newAddress" className="text-sm">Delivery Address *</Label>
                    <Input
                      id="newAddress"
                      value={newCustomerAddress}
                      onChange={(e) => setNewCustomerAddress(e.target.value)}
                      placeholder="Enter delivery address"
                      required
                    />
                  </div>

                  <div className="space-y-2 sm:col-span-2">
                    <Label htmlFor="newEmail" className="text-sm">Email (Optional)</Label>
                    <Input
                      id="newEmail"
                      type="email"
                      value={newCustomerEmail}
                      onChange={(e) => setNewCustomerEmail(e.target.value)}
                      placeholder="customer@example.com"
                    />
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2">
                  <Button type="submit" className="w-full sm:w-auto">
                    Create Customer
                  </Button>
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowNewCustomerForm(false)}
                    className="w-full sm:w-auto"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Pallet Monitoring Form (Admin Only) */}
        {user.role === 'Admin' && selectedCustomer && (
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Pallet Monitoring</CardTitle>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowMonitoringForm(!showMonitoringForm)}
                >
                  {showMonitoringForm ? 'Hide Form' : 'Add Movement'}
                </Button>
              </div>
            </CardHeader>
            {showMonitoringForm && (
              <CardContent>
                <form onSubmit={handleMonitoringSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="date">Date *</Label>
                      <Input
                        id="date"
                        type="date"
                        value={date}
                        onChange={(e) => setDate(e.target.value)}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="colour">Pallet Colour *</Label>
                      <Select value={colour} onValueChange={setColour}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="White">White (Free)</SelectItem>
                          <SelectItem value="Red">Red (£5)</SelectItem>
                          <SelectItem value="Blue">Blue (£5)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="direction">Direction *</Label>
                      <Select value={direction} onValueChange={setDirection}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="OUT">OUT (Taken)</SelectItem>
                          <SelectItem value="IN">IN (Returned)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="quantity">Quantity *</Label>
                      <Input
                        id="quantity"
                        type="number"
                        min="1"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        placeholder="Enter quantity"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="reference">Reference</Label>
                      <Input
                        id="reference"
                        value={reference}
                        onChange={(e) => setReference(e.target.value)}
                        placeholder="Invoice #, etc."
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Input
                        id="notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Optional notes"
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full sm:w-auto">
                    Record Movement
                  </Button>
                </form>
              </CardContent>
            )}
          </Card>
        )}

        {statement && (
          <>
            {/* Total Outstanding Chargeable Pallets */}
            <Card className="border-l-4 border-l-blue-600 bg-gradient-to-r from-blue-50 to-white">
              <CardHeader>
                <CardTitle className="text-base sm:text-lg font-semibold text-slate-900">Total Outstanding Chargeable Pallets</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row sm:items-baseline gap-3 sm:gap-4">
                  <div className="text-4xl sm:text-5xl font-bold text-blue-600" data-testid="total-chargeable">
                    {statement.total_chargeable || 0}
                  </div>
                  <div className="text-xs sm:text-sm text-slate-600">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <span>Red: {statement.balances.Red || 0}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                      <span>Blue: {statement.balances.Blue || 0}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-3 h-3 rounded-full bg-slate-300 border border-slate-400"></div>
                      <span>White: {statement.balances.White || 0} (Free)</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-3">Total chargeable pallets outstanding (Red + Blue only)</p>
              </CardContent>
            </Card>

            {/* Outstanding Amount Summary */}
            <Card className="bg-slate-50 border-2 border-slate-300">
              <CardHeader>
                <CardTitle className="text-base sm:text-lg">Outstanding Amount</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-xs sm:text-sm">
                  <div className="flex justify-between">
                    <span>White Pallets: {statement.balances.White || 0} @ £0.00</span>
                    <span className="font-medium">£0.00</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Red Pallets: {statement.balances.Red || 0} × £4.17</span>
                    <span className="font-medium">£{((statement.balances.Red || 0) * 4.17).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Blue Pallets: {statement.balances.Blue || 0} × £4.17</span>
                    <span className="font-medium">£{((statement.balances.Blue || 0) * 4.17).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-slate-300">
                    <span>Subtotal (exc. VAT):</span>
                    <span className="font-medium">£{(((statement.balances.Red || 0) * 4.17) + ((statement.balances.Blue || 0) * 4.17)).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>VAT (20%):</span>
                    <span className="font-medium">£{((((statement.balances.Red || 0) * 4.17) + ((statement.balances.Blue || 0) * 4.17)) * 0.20).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-3 border-t-2 border-slate-400 text-base sm:text-lg font-bold text-slate-900">
                    <span>TOTAL OWED (inc. VAT):</span>
                    <span>£{(((statement.balances.Red || 0) * 5) + ((statement.balances.Blue || 0) * 5)).toFixed(2)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Statement Table */}
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <CardTitle className="text-base sm:text-lg">Movement History</CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={generateProforma}
                    data-testid="generate-proforma-button"
                    className="w-full sm:w-auto"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Generate Proforma
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full" data-testid="statement-table">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Date</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Reference</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Colour</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">OUT</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">IN</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-slate-600">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statement.statement.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-8 text-slate-500">No movements found</td>
                        </tr>
                      ) : (
                        statement.statement.map((movement, idx) => (
                          <tr key={idx} className="border-b border-slate-100">
                            <td className="py-3 px-4 text-sm">
                              {new Date(movement.date).toLocaleDateString('en-GB')}
                            </td>
                            <td className="py-3 px-4 text-sm">{movement.reference}</td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full ${
                                  movement.colour === 'White' ? 'bg-slate-300 border border-slate-400' :
                                  movement.colour === 'Red' ? 'bg-red-500' :
                                  'bg-blue-500'
                                }`}></div>
                                <span className="text-sm">{movement.colour}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-right font-medium text-red-600">
                              {movement.direction === 'OUT' ? movement.quantity : '-'}
                            </td>
                            <td className="py-3 px-4 text-right font-medium text-green-600">
                              {movement.direction === 'IN' ? movement.quantity : '-'}
                            </td>
                            <td className="py-3 px-4 text-right font-bold text-slate-900">
                              {movement.running_balance}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mobile/Tablet Card View */}
                <div className="md:hidden space-y-3">
                  {statement.statement.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">No movements found</div>
                  ) : (
                    statement.statement.map((movement, idx) => (
                      <Card key={idx} className="border border-slate-200">
                        <CardContent className="p-3">
                          <div className="space-y-2">
                            <div className="flex justify-between items-start">
                              <div className="flex items-center gap-2">
                                <div className={`w-3 h-3 rounded-full ${
                                  movement.colour === 'White' ? 'bg-slate-300 border border-slate-400' :
                                  movement.colour === 'Red' ? 'bg-red-500' :
                                  'bg-blue-500'
                                }`}></div>
                                <span className="text-sm font-medium">{movement.colour}</span>
                              </div>
                              <span className="text-xs text-slate-600">
                                {new Date(movement.date).toLocaleDateString('en-GB')}
                              </span>
                            </div>
                            <div className="text-xs text-slate-600">
                              <span className="font-medium">Ref:</span> {movement.reference}
                            </div>
                            <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 text-xs">
                              <div>
                                <p className="text-slate-500 mb-1">OUT</p>
                                <p className="font-medium text-red-600">
                                  {movement.direction === 'OUT' ? movement.quantity : '-'}
                                </p>
                              </div>
                              <div>
                                <p className="text-slate-500 mb-1">IN</p>
                                <p className="font-medium text-green-600">
                                  {movement.direction === 'IN' ? movement.quantity : '-'}
                                </p>
                              </div>
                              <div>
                                <p className="text-slate-500 mb-1">Balance</p>
                                <p className="font-bold text-slate-900">
                                  {movement.running_balance}
                                </p>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* Archived Customers Section */}
        <Card className="border-2 border-slate-300 bg-slate-50">
          <CardHeader>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <Archive className="h-5 w-5 text-slate-600" />
                <CardTitle className="text-base sm:text-lg text-slate-700">
                  Archived Customers (Zero Outstanding Balance)
                </CardTitle>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setShowArchivedCustomers(!showArchivedCustomers);
                  if (!showArchivedCustomers && archivedCustomers.length === 0) {
                    fetchArchivedCustomers();
                  }
                }}
                className="w-full sm:w-auto"
              >
                {showArchivedCustomers ? (
                  <>
                    <ChevronUp className="h-4 w-4 mr-2" />
                    Hide Archived
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4 mr-2" />
                    Show Archived
                  </>
                )}
              </Button>
            </div>
          </CardHeader>

          {showArchivedCustomers && (
            <CardContent>
              {archivedCustomersLoading ? (
                <div className="text-center py-8 text-slate-500">Loading archived customers...</div>
              ) : archivedCustomers.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No archived customers found. Customers appear here when they have pallet history but zero current balance.
                </div>
              ) : (
                <div className="space-y-4">
                  {archivedCustomers.map((customer) => (
                    <Card key={customer.id} className="border border-slate-300 bg-white">
                      <CardHeader className="pb-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="text-base font-semibold text-slate-900">
                              {customer.business_name}
                            </h3>
                            <p className="text-sm text-slate-600 mt-1">{customer.delivery_address}</p>
                            <div className="flex gap-4 mt-2 text-xs text-slate-500">
                              <span>Red: {customer.balances.Red || 0}</span>
                              <span>Blue: {customer.balances.Blue || 0}</span>
                              <span>White: {customer.balances.White || 0}</span>
                              <span className="font-medium text-green-600">✓ Settled</span>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleArchivedCustomer(customer.id)}
                            className="flex-shrink-0"
                          >
                            {expandedArchived[customer.id] ? (
                              <>
                                <ChevronUp className="h-4 w-4 mr-1" />
                                Hide History
                              </>
                            ) : (
                              <>
                                <ChevronDown className="h-4 w-4 mr-1" />
                                View History
                              </>
                            )}
                          </Button>
                        </div>
                      </CardHeader>

                      {expandedArchived[customer.id] && (
                        <CardContent className="pt-0">
                          <div className="border-t border-slate-200 pt-4">
                            {/* Desktop Table View */}
                            <div className="hidden md:block overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-slate-200 bg-slate-50">
                                    <th className="text-left py-2 px-3 font-medium text-slate-600">Date & Time</th>
                                    <th className="text-left py-2 px-3 font-medium text-slate-600">Reference</th>
                                    <th className="text-left py-2 px-3 font-medium text-slate-600">Colour</th>
                                    <th className="text-right py-2 px-3 font-medium text-slate-600">OUT</th>
                                    <th className="text-right py-2 px-3 font-medium text-slate-600">IN</th>
                                    <th className="text-right py-2 px-3 font-medium text-slate-600">Balance</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {customer.movements.map((movement, idx) => (
                                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                                      <td className="py-2 px-3">
                                        <div>
                                          <div className="font-medium">
                                            {new Date(movement.date).toLocaleDateString('en-GB')}
                                          </div>
                                          <div className="text-xs text-slate-500">
                                            {new Date(movement.date).toLocaleTimeString()}
                                          </div>
                                        </div>
                                      </td>
                                      <td className="py-2 px-3 text-slate-600">{movement.reference}</td>
                                      <td className="py-2 px-3">
                                        <div className="flex items-center gap-2">
                                          <div className={`w-3 h-3 rounded-full ${
                                            movement.colour === 'White' ? 'bg-slate-300 border border-slate-400' :
                                            movement.colour === 'Red' ? 'bg-red-500' :
                                            'bg-blue-500'
                                          }`}></div>
                                          <span>{movement.colour}</span>
                                        </div>
                                      </td>
                                      <td className="py-2 px-3 text-right font-medium text-red-600">
                                        {movement.direction === 'OUT' ? movement.quantity : '-'}
                                      </td>
                                      <td className="py-2 px-3 text-right font-medium text-green-600">
                                        {movement.direction === 'IN' ? movement.quantity : '-'}
                                      </td>
                                      <td className="py-2 px-3 text-right font-semibold text-slate-900">
                                        {movement.running_balance}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            {/* Mobile Card View */}
                            <div className="md:hidden space-y-2">
                              {customer.movements.map((movement, idx) => (
                                <div key={idx} className="border border-slate-200 rounded-lg p-3 bg-white">
                                  <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center gap-2">
                                      <div className={`w-3 h-3 rounded-full ${
                                        movement.colour === 'White' ? 'bg-slate-300 border border-slate-400' :
                                        movement.colour === 'Red' ? 'bg-red-500' :
                                        'bg-blue-500'
                                      }`}></div>
                                      <span className="text-sm font-medium">{movement.colour}</span>
                                    </div>
                                    <div className="text-right">
                                      <div className="text-xs font-medium text-slate-900">
                                        {new Date(movement.date).toLocaleDateString('en-GB')}
                                      </div>
                                      <div className="text-xs text-slate-500">
                                        {new Date(movement.date).toLocaleTimeString()}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-xs text-slate-600 mb-2">
                                    Ref: {movement.reference}
                                  </div>
                                  <div className="grid grid-cols-3 gap-2 text-xs">
                                    <div>
                                      <p className="text-slate-500 mb-1">OUT</p>
                                      <p className="font-medium text-red-600">
                                        {movement.direction === 'OUT' ? movement.quantity : '-'}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-slate-500 mb-1">IN</p>
                                      <p className="font-medium text-green-600">
                                        {movement.direction === 'IN' ? movement.quantity : '-'}
                                      </p>
                                    </div>
                                    <div>
                                      <p className="text-slate-500 mb-1">Balance</p>
                                      <p className="font-semibold text-slate-900">
                                        {movement.running_balance}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          )}
        </Card>
      </div>
    </Layout>
  );
}
